import React, { useState } from 'react'
import type { ValueBetItem, OddsFormat, SupportData } from '../types'
import { fmtKickoff } from '../utils'
import { useOdds } from '../context/OddsContext'
import { useBetBuilder, type SavedBet } from '../context/BetContext'
import { IoChevronDown, IoChevronUp, IoAdd, IoCheckmark } from 'react-icons/io5'

interface ValueBetCardProps {
  bet: ValueBetItem
  isExpanded: boolean
  onToggle: () => void
}

function calculateEdgeFromOdds(odds: number, convictionPct: number): number {
  if (odds <= 1) return 0
  const impliedProb = (1 / odds) * 100
  return convictionPct - impliedProb
}

// Parse user input based on odds format and convert to decimal
function parseUserOddsInput(input: string, format: OddsFormat): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  switch (format) {
    case 'fractional': {
      // Parse fractional odds like "3/2" or "5/1"
      const match = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/)
      if (!match) return null
      const numerator = parseInt(match[1], 10)
      const denominator = parseInt(match[2], 10)
      if (denominator === 0) return null
      // Convert fractional to decimal: (num/den) + 1
      return (numerator / denominator) + 1
    }
    case 'american': {
      // Parse American odds like "+150" or "-200"
      const match = trimmed.match(/^([+-]?\d+)$/)
      if (!match) return null
      const american = parseInt(match[1], 10)
      if (american === 0) return null
      // Convert American to decimal
      if (american > 0) {
        return (american / 100) + 1
      } else {
        return (100 / Math.abs(american)) + 1
      }
    }
    case 'decimal':
    default: {
      // Parse decimal odds like "2.50"
      const decimal = parseFloat(trimmed)
      if (isNaN(decimal)) return null
      return decimal
    }
  }
}

// Get placeholder text based on odds format
function getOddsPlaceholder(format: OddsFormat): string {
  switch (format) {
    case 'fractional':
      return '3/2'
    case 'american':
      return '+150'
    case 'decimal':
    default:
      return '2.50'
  }
}

// Get input pattern hint based on format
function getOddsInputPattern(format: OddsFormat): string {
  switch (format) {
    case 'fractional':
      return '[0-9]+/[0-9]+'
    case 'american':
      return '[+-]?[0-9]+'
    case 'decimal':
    default:
      return '[0-9]+\\.?[0-9]*'
  }
}

function getConfidenceStyle(confidence: string): React.CSSProperties {
  switch (confidence) {
    case 'high':
      return {
        background: 'rgba(74, 222, 128, 0.15)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        color: 'var(--green)',
      }
    case 'med':
      return {
        background: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
        color: 'var(--yellow)',
      }
    default:
      return {
        background: 'rgba(251, 113, 133, 0.15)',
        borderColor: 'rgba(251, 113, 133, 0.4)',
        color: 'var(--red)',
      }
  }
}

function getEdgeClass(edge: number): string {
  if (edge > 0) return 'edge-positive'
  if (edge < -2) return 'edge-negative'
  return 'edge-neutral'
}

// Map value bet market type to SavedBet marketType
function mapMarketType(marketType: string): SavedBet['marketType'] {
  const lower = marketType.toLowerCase()
  if (lower.includes('corner')) return 'corners'
  if (lower.includes('shot') && lower.includes('target')) return 'shots_on_target'
  if (lower.includes('shot')) return 'shots'
  if (lower.includes('card') || lower.includes('yellow') || lower.includes('red')) return 'cards'
  if (lower.includes('goal')) return 'goals'
  return 'other'
}

// Determine team type from selection label
function inferTeamType(selectionLabel: string, fixtureLabel: string): SavedBet['teamType'] {
  const lower = selectionLabel.toLowerCase()
  if (lower.includes('total') || lower.includes('match')) return 'total'

  // Try to match team name from fixture label (e.g., "Team A vs Team B")
  const [homeTeam, awayTeam] = fixtureLabel.split(' vs ').map(t => t.trim().toLowerCase())
  if (homeTeam && lower.includes(homeTeam)) return 'home'
  if (awayTeam && lower.includes(awayTeam)) return 'away'

  return 'total'
}

// Format a multiplier factor for display
function formatFactor(factor: number | undefined): string | null {
  if (factor === undefined || factor === null) return null
  if (Math.abs(factor - 1.0) < 0.01) return null // Don't show if ~1.0
  return factor > 1 ? `+${((factor - 1) * 100).toFixed(0)}%` : `${((factor - 1) * 100).toFixed(0)}%`
}

// Safely format a number with toFixed, handling null/undefined
function safeToFixed(value: number | null | undefined, decimals: number): string {
  if (value === null || value === undefined) return '—'
  return value.toFixed(decimals)
}

// Calculation breakdown component
function CalculationBreakdown({ support, convictionPct, impliedProb, edge }: {
  support: SupportData
  convictionPct: number
  impliedProb: number
  edge: number
}) {
  const hasStatisticalBasis = support.venue_avg != null || support.expected != null
  const hasAdjustments = support.opp_factor != null || support.ref_factor != null || support.is_derby || support.is_relegation
  const hasConfidence = support.matches != null
  const hasXgContext = support.home_xg != null || support.away_xg != null || support.model_xg != null

  return (
    <div className="calculation-breakdown">
      {/* Statistical Basis */}
      {hasStatisticalBasis && (
        <div className="breakdown-section">
          <div className="breakdown-header">Statistical Basis</div>
          <div className="breakdown-content">
            {support.venue_avg != null && (
              <div className="breakdown-row">
                <span className="breakdown-label">Form avg:</span>
                <span className="breakdown-value">{safeToFixed(support.venue_avg, 1)}/game</span>
              </div>
            )}
            {support.season_avg != null && (
              <div className="breakdown-row">
                <span className="breakdown-label">Season avg:</span>
                <span className="breakdown-value">{safeToFixed(support.season_avg, 1)}/game</span>
              </div>
            )}
            {support.opp_stat != null && support.opp_stat_label && (
              <div className="breakdown-row">
                <span className="breakdown-label">Opponent {support.opp_stat_label}:</span>
                <span className="breakdown-value">{safeToFixed(support.opp_stat, 1)}/game</span>
              </div>
            )}
            {support.expected != null && (
              <div className="breakdown-row highlight">
                <span className="breakdown-label">Expected:</span>
                <span className="breakdown-value">{safeToFixed(support.expected, 1)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Adjustment Factors */}
      {hasAdjustments && (
        <div className="breakdown-section">
          <div className="breakdown-header">Adjustment Factors</div>
          <div className="breakdown-content">
            {support.opp_factor != null && formatFactor(support.opp_factor) && (
              <div className="breakdown-row">
                <span className="breakdown-label">Opponent quality:</span>
                <span className={`breakdown-value ${support.opp_factor > 1 ? 'factor-up' : 'factor-down'}`}>
                  {formatFactor(support.opp_factor)}
                </span>
              </div>
            )}
            {support.ref_factor != null && formatFactor(support.ref_factor) && (
              <div className="breakdown-row">
                <span className="breakdown-label">
                  Referee{support.ref_name ? ` (${support.ref_name})` : ''}:
                </span>
                <span className={`breakdown-value ${support.ref_factor > 1 ? 'factor-up' : 'factor-down'}`}>
                  {formatFactor(support.ref_factor)}
                  {support.ref_avg_cards != null && ` (${safeToFixed(support.ref_avg_cards, 1)} cards/game)`}
                </span>
              </div>
            )}
            {(support.is_derby || support.is_relegation) && (
              <div className="breakdown-row">
                <span className="breakdown-label">Match context:</span>
                <span className="breakdown-value">
                  {support.is_derby && <span className="context-badge derby">Derby</span>}
                  {support.is_relegation && <span className="context-badge relegation">Relegation</span>}
                </span>
              </div>
            )}
            {support.adjusted_avg != null && (
              <div className="breakdown-row highlight">
                <span className="breakdown-label">Adjusted avg:</span>
                <span className="breakdown-value">{safeToFixed(support.adjusted_avg, 1)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* xG Context (for goals markets) */}
      {hasXgContext && (
        <div className="breakdown-section xg-section">
          <div className="breakdown-header">Expected Goals (xG)</div>
          <div className="breakdown-content">
            {support.model_xg != null && (
              <div className="breakdown-row highlight">
                <span className="breakdown-label">Model xG:</span>
                <span className="breakdown-value xg-value">{safeToFixed(support.model_xg, 2)}</span>
              </div>
            )}
            {support.home_xg != null && support.away_xg != null && (
              <div className="breakdown-row">
                <span className="breakdown-label">Team xG avg:</span>
                <span className="breakdown-value">
                  {safeToFixed(support.home_xg, 2)} vs {safeToFixed(support.away_xg, 2)}
                </span>
              </div>
            )}
            {support.xg_diff != null && (
              <div className="breakdown-row">
                <span className="breakdown-label">xG vs Line:</span>
                <span className={`breakdown-value ${support.xg_diff > 0 ? 'factor-up' : support.xg_diff < 0 ? 'factor-down' : ''}`}>
                  {support.xg_diff > 0 ? '+' : ''}{safeToFixed(support.xg_diff, 2)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confidence Indicators */}
      {hasConfidence && (
        <div className="breakdown-section">
          <div className="breakdown-header">Confidence</div>
          <div className="breakdown-content">
            {support.matches != null && (
              <div className="breakdown-row">
                <span className="breakdown-label">Sample size:</span>
                <span className="breakdown-value">{support.matches} matches</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Final Calculation */}
      <div className="breakdown-section final-calc">
        <div className="breakdown-header">Final Calculation</div>
        <div className="breakdown-content">
          <div className="breakdown-row">
            <span className="breakdown-label">Our probability:</span>
            <span className="breakdown-value">{convictionPct}%</span>
          </div>
          <div className="breakdown-row">
            <span className="breakdown-label">Implied (from odds):</span>
            <span className="breakdown-value">{impliedProb.toFixed(1)}%</span>
          </div>
          <div className="breakdown-row highlight">
            <span className="breakdown-label">Edge:</span>
            <span className={`breakdown-value ${edge >= 0 ? 'edge-positive' : 'edge-negative'}`}>
              {edge >= 0 ? '+' : ''}{edge.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ValueBetCard({ bet, isExpanded, onToggle }: ValueBetCardProps) {
  const { formatOdds, oddsFormat } = useOdds()
  const { addBet, removeBet, isBetSaved, openModal } = useBetBuilder()
  const [userOddsInput, setUserOddsInput] = useState('')

  const confStyle = getConfidenceStyle(bet.confidence)
  const edgeClass = getEdgeClass(bet.edge)
  const edgeLabel = `${bet.edge >= 0 ? '+' : ''}${bet.edge.toFixed(1)}%`

  // Calculate edge for user's odds (parse based on selected format)
  const userOddsDecimal = parseUserOddsInput(userOddsInput, oddsFormat)
  const userEdge = userOddsDecimal !== null && userOddsDecimal > 1
    ? calculateEdgeFromOdds(userOddsDecimal, bet.conviction_pct)
    : null

  // Generate unique bet ID for builder
  const betId = `valueBet-${bet.fixture_id}-${bet.market_type}-${bet.selection_label}-${bet.outcome}`
  const isAdded = isBetSaved(betId)

  // Determine if this is an over/under bet
  const isOver = bet.outcome.toLowerCase() === 'over'

  const handleToggleBet = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card expansion
    if (isAdded) {
      removeBet(betId)
    } else {
      addBet({
        id: betId,
        fixtureId: bet.fixture_id,
        fixture: bet.fixture_label,
        title: bet.selection_label,
        line: bet.line ?? undefined,
        marketType: mapMarketType(bet.market_type),
        scope: bet.market_category.toLowerCase().includes('player') ? 'player' : 'match',
        teamType: inferTeamType(bet.selection_label, bet.fixture_label),
        isOver,
        confidence: bet.confidence,
        confidencePct: bet.conviction_pct,
        odds: bet.best_odds,
      })
      openModal()
    }
  }

  return (
    <div
      className={`value-bet-card ${isExpanded ? 'expanded' : ''}`}
      onClick={onToggle}
    >
      {/* Compact Header - Always Visible */}
      <div className="value-bet-compact">
        <div className="value-bet-main">
          <div className="value-bet-edge">
            <span
              className={`edge-badge ${edgeClass}`}
              title={`Edge: Our probability (${bet.conviction_pct}%) minus implied probability (${bet.implied_probability.toFixed(1)}%)`}
            >
              {edgeLabel}
            </span>
          </div>
          <div className="value-bet-selection">
            <span className="selection-label">{bet.selection_label}</span>
            <span className="fixture-label">{bet.fixture_label}</span>
            {/* Context summary - always visible */}
            {bet.context_summary && (
              <span className="context-summary">{bet.context_summary}</span>
            )}
          </div>
          <div className="value-bet-odds">
            <span
              className="odds-value"
              title={`Best odds: ${bet.best_odds.toFixed(2)} decimal (implies ${bet.implied_probability.toFixed(1)}% chance)`}
            >
              {formatOdds(bet.best_odds)}
            </span>
            <span className="bookmaker-name">{bet.best_bookmaker_name}</span>
          </div>
          <button
            className={`add-to-builder-btn ${isAdded ? 'added' : ''}`}
            onClick={handleToggleBet}
            title={isAdded ? 'Remove from builder' : 'Add to builder'}
          >
            {isAdded ? <IoCheckmark size={14} /> : <IoAdd size={14} />}
            {isAdded ? 'Added' : 'Add'}
          </button>
        </div>
        <div className="value-bet-meta">
          <span
            className="pill conviction-pill"
            style={{
              ...confStyle,
              border: `1px solid ${confStyle.borderColor}`,
            }}
            title={`Our calculated probability: ${bet.conviction_pct}% chance this bet wins`}
          >
            {bet.conviction_pct}%
          </span>
          <span className="kickoff-time">{fmtKickoff(bet.kickoff_utc)}</span>
          <span className="league-badge">{bet.league_name}</span>
          <span className="expand-icon">
            {isExpanded ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
          </span>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="value-bet-details">
          {/* Calculation Breakdown - show if we have support data */}
          {bet.support_data && (
            <CalculationBreakdown
              support={bet.support_data}
              convictionPct={bet.conviction_pct}
              impliedProb={bet.implied_probability}
              edge={bet.edge}
            />
          )}

          {/* Fallback to basic details if no support data */}
          {!bet.support_data && (
            <>
              <div className="detail-row">
                <span className="detail-label">Market</span>
                <span className="detail-value">{bet.market_category}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Our Probability</span>
                <span className="detail-value">{bet.conviction_pct}%</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Implied Probability</span>
                <span className="detail-value">{bet.implied_probability.toFixed(1)}%</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Edge</span>
                <span className={`detail-value ${edgeClass}`}>{edgeLabel}</span>
              </div>
            </>
          )}

          {/* All Bookmaker Odds */}
          {bet.all_odds && bet.all_odds.length > 0 && (
            <div className="bookmaker-odds-section">
              <div className="bookmaker-odds-header">All Bookmaker Odds</div>
              <div className="bookmaker-odds-list">
                {bet.all_odds.map((bkOdds, idx) => (
                  <div
                    key={`${bkOdds.bookmaker_slug}-${idx}`}
                    className={`bookmaker-odds-row ${idx === 0 ? 'best' : ''}`}
                  >
                    <span className="bk-name">{bkOdds.bookmaker_name}</span>
                    <span className="bk-odds">{formatOdds(bkOdds.odds)}</span>
                    <span className={`bk-edge ${bkOdds.edge >= 0 ? 'positive' : 'negative'}`}>
                      {bkOdds.edge >= 0 ? '+' : ''}{bkOdds.edge.toFixed(1)}%
                    </span>
                  </div>
                ))}

                {/* User's custom odds input */}
                <div
                  className={`bookmaker-odds-row user-odds-row ${userEdge !== null && userEdge > 0 ? 'value' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="bk-name">Your Odds</span>
                  <input
                    type="text"
                    inputMode={oddsFormat === 'decimal' ? 'decimal' : 'text'}
                    placeholder={getOddsPlaceholder(oddsFormat)}
                    pattern={getOddsInputPattern(oddsFormat)}
                    value={userOddsInput}
                    onChange={(e) => setUserOddsInput(e.target.value)}
                    className="user-odds-input"
                  />
                  <span className={`bk-edge ${userEdge !== null ? (userEdge >= 0 ? 'positive' : 'negative') : ''}`}>
                    {userEdge !== null
                      ? `${userEdge >= 0 ? '+' : ''}${userEdge.toFixed(1)}%`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {bet.why && (
            <div className="detail-why">
              <span className="why-label">Analysis:</span>
              <span className="why-text">{bet.why}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
